import { Injectable } from "@angular/core";
import {
  IDevicesAvailableToOrder,
  IDevicesOrder,
  INewPrescription,
  IOfficialCareplanDetails,
  IPractitionerTemplate,
  IPrescription,
  IPrescriptionInfosResponse,
  IPrescriptionToBeRenewedInfosResponse,
  IPricingConfig,
  OnlineDevice,
  PRESCRIPTION_TYPE,
  RENEW_TIMING,
  TemplateDocCategory,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

@Injectable({
  providedIn: "root",
})
export class PrescriptionApiService extends BaseApiService {
  constructor(private api: ApiService) {
    super(
      "Prescription", // Theme !
      ["dashboard/acceptPrescription", "dashboard/prescription", "dashboard/orderDevices", "dashboard/practitionerTemplate"], // Post route paths
      [
        "dashboard/prescriptionInfos",
        "dashboard/nbPrescriptionInfos",
        "dashboard/prescriptionsToBeRenewedInfos",
        "dashboard/nbPrescriptionsToBeRenewed",
        "dashboard/prescriptionOfficialCareplan",
        "dashboard/devicesToOrder",
        "dashboard/practitionerTemplates",
        "dashboard/practitionerTemplate/autoCompleted",
        "dashboard/prescription",
        "dashboard/prescriptionConfig",
      ], // Get route paths
      ["dashboard/prescription"], // Update route paths
      ["dashboard/practitionerTemplate"] // Delete route paths
    );
  }

  public getPrescriptionInfos(params): Observable<IPrescriptionInfosResponse[]> {
    if (params?.filters?.length) {
      params.filters = JSON.stringify(params.filters);
    }
    return this.api.get(this.readRoutes[0], params) as Observable<IPrescriptionInfosResponse[]>;
  }

  public acceptPrescription(prescriptionId: string, careplanId?: string): Observable<unknown> {
    const params = { prescriptionId, careplanId };
    return this.api.post(this.createRoutes[0], params) as Observable<unknown>;
  }

  public getPrescriptionInfosCount(
    patientCaremateId?: string,
    services?: string[],
    organizations?: string[],
    period?: string,
    date?: string,
    status?: number[]
  ): Observable<{
    count: number;
  }> {
    const params = { patientCaremateId, services, organizations, period, date, status: status };
    return this.api.get(this.readRoutes[1], params) as Observable<{
      count: number;
    }>;
  }

  public getPrescriptionsToBeRenewedInfos(
    timing: RENEW_TIMING,
    pageNumber: number,
    pageSize: number,
    services?: string
  ): Observable<IPrescriptionToBeRenewedInfosResponse[]> {
    const params = { timing, pageNumber, pageSize, services };
    return this.api.get(this.readRoutes[2], params) as Observable<IPrescriptionToBeRenewedInfosResponse[]>;
  }

  public getPrescriptionsToBeRenewedCount(
    timing: RENEW_TIMING,
    services?: string[]
  ): Observable<{
    count: number;
  }> {
    const params = { timing, services };
    return this.api.get(this.readRoutes[3], params) as Observable<{
      count: number;
    }>;
  }

  public createPrescription(prescription: INewPrescription, type: PRESCRIPTION_TYPE): Observable<IPrescription> {
    return this.api.post(this.createRoutes[1], prescription, { type }) as Observable<IPrescription>;
  }

  public getPrescriptionsOfficialCareplan(identifier: string): Observable<IOfficialCareplanDetails> {
    const params = { identifier };
    return this.api.get(this.readRoutes[4], params) as Observable<IOfficialCareplanDetails>;
  }

  public getDevicesToOrder(deviceType: OnlineDevice, externalRessourceRefs?: string[]): Observable<IDevicesAvailableToOrder[]> {
    const params = { deviceType, externalRessourceRefs: externalRessourceRefs?.length ? externalRessourceRefs.join(",") : undefined };
    return this.api.get(this.readRoutes[5], params) as Observable<IDevicesAvailableToOrder[]>;
  }

  public orderDevice(
    patientId: string,
    orderName: string,
    estimatedDays: number,
    city: string,
    country: string,
    streetName: string,
    streetNumber: string,
    postalCode: string,
    devicesSetName: string,
    devicesSetTypes: string[],
    devicesBrand: OnlineDevice,
    boxNumber?: string,
    additionalStuff?: number,
    externalRessourceRef?: string
  ): Observable<IDevicesOrder> {
    const body = {
      patientId,
      orderName,
      estimatedDays,
      city,
      country,
      streetName,
      streetNumber,
      postalCode,
      devicesSetName,
      devicesSetTypes,
      devicesBrand,
      boxNumber,
      additionalStuff,
      externalRessourceRef,
    };
    return this.api.post(this.createRoutes[2], body) as Observable<IDevicesOrder>;
  }

  public getPrescriptionTemplates(serviceId: string): Observable<IPractitionerTemplate[]> {
    const params = {
      serviceId,
      type: TemplateDocCategory.SIGNATURE_TLS,
    };
    return this.api.get(this.readRoutes[6], params) as Observable<IPractitionerTemplate[]>;
  }

  public createPrescriptionTemplates(
    file: any,
    practitionerId: string,
    type: TemplateDocCategory,
    availableToService = false
  ): Observable<IPractitionerTemplate> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("practitionerId", practitionerId);
    formData.append("type", type);
    formData.append("availableToService", availableToService ? "true" : "false");
    return this.api.post(this.createRoutes[3], formData, null, true) as Observable<IPractitionerTemplate>;
  }

  public deletePrescriptionTemplate(identifier: string): Observable<void> {
    return this.api.deleteWithParams(this.deleteRoutes[0], { identifier }) as Observable<void>;
  }

  public autoCompletePrescriptionTemplate(templateId: string, patientId: string, lang: string, duration: number): Observable<any> {
    const params = {
      templateId,
      patientId,
      lang,
      duration,
    };
    return this.api.getBlob(this.readRoutes[7], params) as Observable<Blob>;
  }

  public getPrescription(identifier: string): Observable<IPrescription> {
    const params = {
      identifier,
    };
    return this.api.get(this.readRoutes[8], params) as Observable<IPrescription>;
  }

  public getPrescriptionConfig(identifier: string, lang: string): Observable<IPricingConfig> {
    const params = {
      identifier,
      lang,
    };
    return this.api.get(this.readRoutes[9], params) as Observable<IPricingConfig>;
  }

  public editPrescription(prescription: IPrescription, config: IPricingConfig): Observable<IPrescription> {
    return this.api.put(this.updateRoutes[0], { prescription, config }) as Observable<IPrescription>;
  }
}
